/* eslint-disable sort-imports */
/**
 * One operation (step) of the standard in the form: its name, type and
 * notes; where it runs (work center + alternatives with their own minutes);
 * how it runs (on its own or in a parallel group); and its phases.
 */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
	Box,
	Checkbox,
	Chip,
	FormControl,
	Grid2,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback } from "react";

import { OperationType } from "../../../../services/labor-standards-api";
import { WorkPhase } from "../../../../services/work-phases-api";
import { GhostButton, planner,SectionLabel } from "../../planner-ui";
import { OperationFieldValue, PhaseFieldValue } from "../controller/use-standard-form";
import {
	NEW_PARALLEL_GROUP,
	nextParallelGroupId,
	OperationFormRow,
	parallelGroupLabel,
	PhaseFormRow,
	WorkstationOptionFormRow,
} from "../utils/standard-helpers";
import PhaseRow, { MasterJob } from "./phase-row";
import { deleteIconButtonSx, fieldSx, menuItemSx, monoFieldSx, sequenceBadgeSx } from "./styles";

/** The minutes override for one alternative work center. */
const OptionMinutesField: React.FC<{
	option: WorkstationOptionFormRow;
	optionIndex: number;
	options: WorkstationOptionFormRow[];
	onChange: (options: WorkstationOptionFormRow[]) => void;
}> = ({ option, optionIndex, options, onChange }) => {
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) =>
			onChange(options.map((existing, index) => (index === optionIndex ? { ...existing, durationMinutes: e.target.value } : existing))),
		[onChange, options, optionIndex]
	);
	return (
		<Grid2 size={{ xs: 12, sm: 4 }}>
			<TextField
				fullWidth
				size="small"
				label={`Minutes on ${option.workstation}`}
				type="number"
				value={option.durationMinutes}
				placeholder="Same as default"
				onChange={handleChange}
				inputProps={{ min: 1 }}
				helperText="Override when this work center runs the step faster or slower"
				sx={monoFieldSx}
			/>
		</Grid2>
	);
};

const renderAlternatives = (selected: unknown) => (
	<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
		{(selected as string[]).map((name) => (
			<Chip key={name} label={name} size="small" sx={{ borderRadius: `${planner.radiusSm}px`, fontFamily: planner.fontUi, fontSize: 12 }} />
		))}
	</Box>
);

/** The operations that share this one's group, by name or position. */
const partnersOf = (operations: OperationFormRow[], opIndex: number, group: number): string[] =>
	operations
		.map((other, index) => ({ other, index }))
		.filter(({ other, index }) => index !== opIndex && other.parallelGroup === group)
		.map(({ other, index }) => other.name.trim() || `operation ${index + 1}`);

export interface OperationEditorProps {
	opIndex: number;
	operation: OperationFormRow;
	/** Every operation on the form — for the partners text and the next group id. */
	operations: OperationFormRow[];
	operationTypes: OperationType[];
	workcenters: string[];
	jobRoles: MasterJob[];
	workPhases: WorkPhase[];
	usedParallelGroups: number[];
	onChange: (opIndex: number, field: keyof OperationFormRow, value: OperationFieldValue) => void;
	onRemove: (opIndex: number) => void;
	onAddPhase: (opIndex: number) => void;
	onRemovePhase: (opIndex: number, phaseIndex: number) => void;
	onChangePhase: (opIndex: number, phaseIndex: number, field: keyof PhaseFormRow, value: PhaseFieldValue) => void;
}

const OperationEditor: React.FC<OperationEditorProps> = ({
	opIndex,
	operation,
	operations,
	operationTypes,
	workcenters,
	jobRoles,
	workPhases,
	usedParallelGroups,
	onChange,
	onRemove,
	onAddPhase,
	onRemovePhase,
	onChangePhase,
}) => {
	const handleName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, "name", e.target.value), [onChange, opIndex]);
	const handleType = useCallback((e: SelectChangeEvent<string>) => onChange(opIndex, "operationTypeId", e.target.value), [onChange, opIndex]);
	const handleNotes = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, "notes", e.target.value), [onChange, opIndex]);
	const handleWorkstation = useCallback((e: SelectChangeEvent<string>) => onChange(opIndex, "workstation", e.target.value), [onChange, opIndex]);
	const handleOptions = useCallback((options: WorkstationOptionFormRow[]) => onChange(opIndex, "workstationOptions", options), [onChange, opIndex]);
	const handleAlternatives = useCallback(
		(e: SelectChangeEvent<string[]>) => {
			const chosen = (typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value) as string[];
			handleOptions(
				chosen.map(
					(name) => operation.workstationOptions.find((option) => option.workstation === name) || { workstation: name, durationMinutes: "" }
				)
			);
		},
		[handleOptions, operation.workstationOptions]
	);
	const handleRuns = useCallback(
		(e: SelectChangeEvent<string>) => {
			const raw = e.target.value;
			const next = raw === "" ? null : raw === NEW_PARALLEL_GROUP ? nextParallelGroupId(operations) : parseInt(raw);
			onChange(opIndex, "parallelGroup", next);
		},
		[onChange, opIndex, operations]
	);
	const handleRemove = useCallback(() => onRemove(opIndex), [onRemove, opIndex]);
	const handleAddPhase = useCallback(() => onAddPhase(opIndex), [onAddPhase, opIndex]);

	const partners = operation.parallelGroup != null ? partnersOf(operations, opIndex, operation.parallelGroup) : [];

	return (
		<Box
			sx={{
				p: 2,
				mb: 2,
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
				borderRadius: `${planner.radius}px`,
				boxShadow: planner.shadowRest,
			}}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Box sx={sequenceBadgeSx}>{opIndex + 1}</Box>
					<Typography sx={{ ...planner.type.title, fontFamily: planner.fontUi, color: planner.ink }}>Operation {opIndex + 1}</Typography>
				</Box>
				<IconButton size="small" onClick={handleRemove} sx={deleteIconButtonSx}>
					<DeleteIcon fontSize="small" />
				</IconButton>
			</Box>

			<Grid2 container spacing={2} sx={{ mb: 2 }}>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					{/* What this operation is called on the floor. Blank falls back to the type. */}
					<TextField fullWidth label="Operation name" placeholder="e.g. Bed cap" value={operation.name} onChange={handleName} sx={fieldSx} />
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<FormControl fullWidth required sx={fieldSx}>
						<InputLabel>Operation Type</InputLabel>
						<Select value={operation.operationTypeId} onChange={handleType} label="Operation Type">
							{operationTypes.map((ot) => (
								<MenuItem key={ot.id} value={ot.id.toString()} sx={menuItemSx}>
									{ot.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<TextField fullWidth label="Notes" value={operation.notes} onChange={handleNotes} sx={fieldSx} />
				</Grid2>
			</Grid2>

			{/* Where the step runs. The work center is on the OPERATION: a recipe
			    is granulation on the FBD, blending on the 75 CFT, compression on
			    the Fette — one standard, not three. Alternatives are the other
			    work centers the step may be planned on, each with its own time. */}
			<Grid2 container spacing={2} sx={{ mb: 1.5 }}>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<FormControl fullWidth required sx={fieldSx}>
						<InputLabel>Work center</InputLabel>
						<Select value={operation.workstation} onChange={handleWorkstation} label="Work center">
							{workcenters.map((wc) => (
								<MenuItem key={wc} value={wc} sx={menuItemSx}>
									{wc}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 8 }}>
					<FormControl fullWidth sx={fieldSx}>
						<InputLabel>Alternative work centers</InputLabel>
						<Select
							multiple
							value={operation.workstationOptions.map((option) => option.workstation)}
							label="Alternative work centers"
							onChange={handleAlternatives}
							renderValue={renderAlternatives}
						>
							{workcenters
								.filter((wc) => wc !== operation.workstation)
								.map((wc) => (
									<MenuItem key={wc} value={wc} sx={menuItemSx}>
										<Checkbox
											size="small"
											checked={operation.workstationOptions.some((option) => option.workstation === wc)}
											sx={{ p: 0.5, mr: 1 }}
										/>
										{wc}
									</MenuItem>
								))}
						</Select>
					</FormControl>
				</Grid2>
				{operation.workstationOptions.map((option, optionIndex) => (
					<OptionMinutesField
						key={option.workstation}
						option={option}
						optionIndex={optionIndex}
						options={operation.workstationOptions}
						onChange={handleOptions}
					/>
				))}
			</Grid2>

			{/* Concurrency. Operations sharing a group run at the same time and
			    need NOT be adjacent — the id is the grouping, not the row
			    position. Picking a group is what moves an operation into that
			    slot in the schedule. */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
				<FormControl size="small" sx={{ ...fieldSx, minWidth: 190 }}>
					<InputLabel>Runs</InputLabel>
					<Select label="Runs" value={operation.parallelGroup == null ? "" : String(operation.parallelGroup)} onChange={handleRuns}>
						<MenuItem value="" sx={menuItemSx}>
							On its own
						</MenuItem>
						{usedParallelGroups.map((group) => (
							<MenuItem key={group} value={String(group)} sx={menuItemSx}>
								In parallel group {parallelGroupLabel(group)}
							</MenuItem>
						))}
						<MenuItem value={NEW_PARALLEL_GROUP} sx={menuItemSx}>
							New parallel group…
						</MenuItem>
					</Select>
				</FormControl>

				{operation.parallelGroup != null && (
					<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
						{partners.length
							? `Runs alongside ${partners.join(", ")} — the step takes as long as the longest of them`
							: "Add another operation to this group, or it saves as sequential"}
					</Typography>
				)}
			</Box>

			{/* Phases */}
			<Box sx={{ pl: 2, borderLeft: `3px solid ${planner.accentBorder}` }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
					<SectionLabel>Phases (job assignments)</SectionLabel>
					<GhostButton size="small" startIcon={<AddIcon />} onClick={handleAddPhase}>
						Add Phase
					</GhostButton>
				</Box>

				{operation.phases.map((phase, phaseIndex) => (
					<PhaseRow
						// Rows have no id of their own until saved; position is their identity here.
						key={phaseIndex} // eslint-disable-line react/no-array-index-key
						opIndex={opIndex}
						phaseIndex={phaseIndex}
						phase={phase}
						jobRoles={jobRoles}
						workPhases={workPhases}
						canRemove={operation.phases.length > 1}
						onChange={onChangePhase}
						onRemove={onRemovePhase}
					/>
				))}
			</Box>
		</Box>
	);
};

export default OperationEditor;
